import { useEffect, useMemo, useState, useCallback } from "react";
import { supplierPOAPI, supplierDeliveryAPI } from "../services/supplierApi";
import { ShoppingCart, Loader2, ChevronLeft, AlertCircle, Info, Package, XCircle, Truck } from "lucide-react";
import Swal from "../../utils/apparifySwal";
import { fmtPeso } from "../../utils/formatters";
import { StatusBadge } from "../../components/erp";
import { getDeliveryLabel, getPOLabel } from "../../utils/documentLabels";

const CARRIER_OPTIONS = [
  "J&T Express",
  "LBC Express",
  "Ninja Van",
  "Flash Express",
  "In-house Delivery",
  "Other",
];

const OPEN_RECEIVING_STATUSES = new Set([
  "PENDING_RECEIPT",
  "PARTIALLY_RECEIVED",
  "RECEIVED_WITH_DISCREPANCY",
]);

function getAvailableToShip(line) {
  const explicit = Number(line.availableToShipQuantity ?? line.available_to_ship_quantity ?? NaN);
  if (Number.isFinite(explicit)) return Math.max(explicit, 0);
  const ordered = Number(line.orderedQuantity || line.ordered_quantity || 0);
  const accepted = Number(line.acceptedQuantity || line.accepted_quantity || 0);
  return Math.max(ordered - accepted, 0);
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

export default function SupplierOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [poDeliveries, setPoDeliveries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [creatingDelivery, setCreatingDelivery] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    carrierPreset: "",
    customCarrier: "",
    notes: "",
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supplierPOAPI.list({ limit: 200 });
      setOrders(res?.data || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const loadPODetail = useCallback(async (poId) => {
    setDetailLoading(true);
    try {
      const [poRes, deliveriesRes] = await Promise.all([
        supplierPOAPI.getById(poId),
        supplierDeliveryAPI.list({ purchaseOrderId: poId, limit: 100 }),
      ]);
      setDetail(poRes?.data || null);
      setPoDeliveries(deliveriesRes?.data || []);
    } catch {
      setDetail(null);
      setPoDeliveries([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetail = async (po) => {
    setSelected(po);
    setDetail(null);
    setDeliveryForm({ carrierPreset: "", customCarrier: "", notes: "" });
    await loadPODetail(po.id);
  };

  const shipmentPlan = useMemo(
    () => (detail?.lines || [])
      .map((line) => ({
        ...line,
        availableToShip: getAvailableToShip(line),
      }))
      .filter((line) => line.availableToShip > 0),
    [detail],
  );

  const activeDelivery = useMemo(
    () => poDeliveries.find((delivery) =>
      delivery.status !== "CANCELLED"
      && OPEN_RECEIVING_STATUSES.has(delivery.receiving_status || "PENDING_RECEIPT")
    ) || null,
    [poDeliveries],
  );

  const resolvedCarrier = deliveryForm.carrierPreset === "Other"
    ? deliveryForm.customCarrier.trim()
    : deliveryForm.carrierPreset.trim();

  const canCreateDelivery = Boolean(
    detail
    && ["SENT_TO_SUPPLIER", "ACKNOWLEDGED", "PARTIALLY_FULFILLED"].includes(detail.status)
    && shipmentPlan.length
    && !activeDelivery,
  );

  const handleDecline = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "Decline PO?",
      text: "Tell the business why this purchase order cannot be fulfilled.",
      input: "textarea",
      inputPlaceholder: "Reason for declining",
      inputAttributes: { maxlength: 500 },
      showCancelButton: true,
      confirmButtonText: "Decline Order",
      confirmButtonColor: "#dc2626",
    });
    if (!isConfirmed) return;

    setDeclining(true);
    try {
      await supplierPOAPI.decline(selected.id, { notes: value || undefined });
      await fetchList();
      await loadPODetail(selected.id);
      Swal.fire({ icon: "success", title: "Purchase order declined", timer: 1600, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err?.message || "Could not decline PO" });
    } finally {
      setDeclining(false);
    }
  };

  const handleCreateDelivery = async () => {
    if (!detail) return;
    if (!resolvedCarrier) {
      Swal.fire({
        icon: "warning",
        title: "Carrier required",
        text: "Choose a carrier or enter a custom carrier before creating the delivery.",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Create delivery?",
      text: detail.status === "SENT_TO_SUPPLIER"
        ? "This will acknowledge the PO automatically and create one draft delivery from the remaining shippable quantities."
        : "This will create one draft delivery from the remaining shippable quantities.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Create Delivery",
      confirmButtonColor: "#2563eb",
    });
    if (!confirm.isConfirmed) return;

    setCreatingDelivery(true);
    try {
      await supplierPOAPI.createDelivery(detail.id, {
        carrier: resolvedCarrier,
        notes: deliveryForm.notes.trim() || undefined,
      });
      setDeliveryForm({ carrierPreset: "", customCarrier: "", notes: "" });
      await loadPODetail(detail.id);
      await fetchList();
      Swal.fire({
        icon: "success",
        title: "Delivery created",
        text: "The PO is now consumed into a draft delivery. Continue shipment progress from the Deliveries page.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err?.message || "Could not create delivery" });
    } finally {
      setCreatingDelivery(false);
    }
  };

  if (selected) {
    return (
      <div className="operations-page max-w-5xl">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
          <ChevronLeft className="h-3 w-3" /> Back to Orders
        </button>

        {detailLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : !detail ? (
          <div className="rounded-xl bg-red-50 p-4 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> Could not load PO details.
          </div>
        ) : (
          <>
            <div className="operations-surface p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">{getPOLabel(detail)}</h2>
                <StatusBadge status={detail.status} context="supplier" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-neutral-400 mb-0.5">Business</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{detail.business_name || "-"}</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Quantity</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{detail.quantity ?? "-"} units</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Unit Price</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{fmtPeso(detail.unit_price_cents)}</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Total</p>
                  <p className="font-bold text-neutral-900 dark:text-neutral-50">{detail.total_price_cents ? fmtPeso(detail.total_price_cents) : fmtPeso(detail.total_amount_cents)}</p>
                </div>
              </div>

              {Array.isArray(detail.lines) && detail.lines.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-neutral-200 dark:border-neutral-700 text-left text-neutral-500 dark:text-neutral-400">
                        <th className="pb-2 pr-3 font-medium">Variant</th>
                        <th className="pb-2 pr-3 font-medium text-right">Ordered</th>
                        <th className="pb-2 pr-3 font-medium text-right">Accepted</th>
                        <th className="pb-2 pr-3 font-medium text-right">Pending Shipment</th>
                        <th className="pb-2 pr-3 font-medium text-right">Available to Ship</th>
                        <th className="pb-2 pr-3 font-medium text-right">Unit Price</th>
                        <th className="pb-2 font-medium text-right">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines.map((line) => (
                        <tr key={line.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                          <td className="py-2 pr-3 text-neutral-700 dark:text-neutral-200">{line.variantTitle || line.variant_title || line.variantSku || "Requested item"}</td>
                          <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{line.orderedQuantity || line.ordered_quantity}</td>
                          <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{line.acceptedQuantity || line.accepted_quantity || 0}</td>
                          <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{line.pendingDeliveryQuantity || line.pending_delivery_quantity || 0}</td>
                          <td className="py-2 pr-3 text-right font-semibold text-neutral-900 dark:text-neutral-50">{getAvailableToShip(line)}</td>
                          <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{fmtPeso(line.unitPriceCents || line.unit_price_cents)}</td>
                          <td className="py-2 text-right font-semibold text-neutral-900 dark:text-neutral-50">{fmtPeso(line.lineTotalCents || line.line_total_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {detail.expected_delivery_at && (
                <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                  Expected delivery: <strong className="text-neutral-700 dark:text-neutral-200">{new Date(detail.expected_delivery_at).toLocaleDateString()}</strong>
                </div>
              )}
              {detail.quoted_delivery_days != null && (
                <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Promised lead time: <strong className="text-neutral-700 dark:text-neutral-200">{detail.quoted_delivery_days} days</strong>
                </div>
              )}

              {detail.notes && (
                <div className="mt-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-3 text-xs text-neutral-600 dark:text-neutral-300">
                  <p className="text-[10px] text-neutral-400 uppercase tracking-wide font-medium mb-1">Notes</p>
                  {detail.notes}
                </div>
              )}
            </div>

            {detail.status === "SENT_TO_SUPPLIER" && (
              <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-5">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">Supplier response</h3>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Creating a delivery will acknowledge this PO automatically. Decline is still available until the first delivery is created.
                    </p>
                    <button onClick={handleDecline} disabled={declining} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition disabled:opacity-50 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300 dark:hover:bg-red-950/20">
                      {declining ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                      {declining ? "Declining..." : "Decline Order"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {detail.status === "SUPPLIER_DECLINED" && (
              <div className="rounded-xl bg-rose-50 dark:bg-rose-950/20 p-4 text-xs text-rose-700 dark:text-rose-300 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                This purchase order was declined on the supplier side.
              </div>
            )}

            {["SENT_TO_SUPPLIER", "ACKNOWLEDGED", "PARTIALLY_FULFILLED"].includes(detail.status) && (
              <div className="operations-surface space-y-4 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">Create Delivery</h3>
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      Shipment quantities are carried over from the remaining PO balance. This form creates one draft delivery from the outstanding lines.
                    </p>
                  </div>
                  {activeDelivery && (
                    <div className="rounded-xl bg-blue-50 px-3 py-2 text-[11px] text-blue-700 dark:bg-blue-950/20 dark:text-blue-300">
                      Active delivery {getDeliveryLabel(activeDelivery)}
                    </div>
                  )}
                </div>

                {activeDelivery ? (
                  <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-4 text-xs text-blue-700 dark:text-blue-300">
                    This PO is already consumed into delivery {getDeliveryLabel(activeDelivery)}. Finish or cancel that delivery before creating another shipment from this order.
                  </div>
                ) : !shipmentPlan.length ? (
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4 text-xs text-neutral-600 dark:text-neutral-300">
                    No remaining shipment quantity is available on this PO.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Carrier *</label>
                        <select
                          value={deliveryForm.carrierPreset}
                          onChange={(e) => setDeliveryForm((current) => ({ ...current, carrierPreset: e.target.value, customCarrier: e.target.value === "Other" ? current.customCarrier : "" }))}
                          className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                        >
                          <option value="">Select carrier...</option>
                          {CARRIER_OPTIONS.map((carrier) => (
                            <option key={carrier} value={carrier}>{carrier}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Expected Arrival</label>
                        <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-200">
                          {formatDate(detail.expected_delivery_at)}
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Tracking Reference</label>
                        <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-300">
                          Auto-generated on delivery creation
                        </div>
                      </div>
                    </div>

                    {deliveryForm.carrierPreset === "Other" && (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Custom Carrier *</label>
                        <input value={deliveryForm.customCarrier} onChange={(e) => setDeliveryForm((current) => ({ ...current, customCarrier: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" placeholder="Enter carrier name" />
                      </div>
                    )}

                    <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                      <table className="w-full text-xs">
                        <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 dark:text-neutral-400">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Variant</th>
                            <th className="px-3 py-2 text-right font-medium">Outstanding to Ship</th>
                            <th className="px-3 py-2 text-right font-medium">Shipment Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shipmentPlan.map((line) => (
                            <tr key={line.id} className="border-t border-neutral-100 dark:border-neutral-800">
                              <td className="px-3 py-3 text-neutral-800 dark:text-neutral-100">{line.variantTitle || line.variant_title || line.variantSku || "Requested item"}</td>
                              <td className="px-3 py-3 text-right text-neutral-700 dark:text-neutral-200">{line.availableToShip}</td>
                              <td className="px-3 py-3 text-right font-semibold text-neutral-900 dark:text-neutral-50">{line.availableToShip}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Supplier Note</label>
                      <textarea rows={2} value={deliveryForm.notes} onChange={(e) => setDeliveryForm((current) => ({ ...current, notes: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" placeholder="Optional note for this shipment" />
                    </div>

                    <div className="flex justify-end">
                      <button onClick={handleCreateDelivery} disabled={creatingDelivery || !canCreateDelivery || !resolvedCarrier} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50">
                        {creatingDelivery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                        {creatingDelivery ? "Creating..." : "Create Delivery"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="operations-surface p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">Deliveries for This PO</h3>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Existing shipments created against this purchase order.</p>
                </div>
                <span className="text-xs text-neutral-400">{poDeliveries.length} total</span>
              </div>

              {poDeliveries.length === 0 ? (
                <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 p-6 text-center text-xs text-neutral-500 dark:text-neutral-400">
                  No deliveries created for this PO yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {poDeliveries.map((delivery) => (
                    <div key={delivery.id} className="rounded-xl border border-neutral-200 dark:border-neutral-700 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getDeliveryLabel(delivery)}</p>
                          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{delivery.carrier || "No carrier"}{delivery.tracking_number ? ` | ${delivery.tracking_number}` : ""}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          <StatusBadge status={delivery.status} context="supplier" />
                          <StatusBadge status={delivery.receiving_status || "PENDING_RECEIPT"} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="operations-page">
      <div className="operations-page-header">
        <div>
          <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Purchase Orders</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Create supplier deliveries directly from the PO. Shipment quantities and expected arrival are carried forward automatically.</p>
        </div>
        <span className="text-xs text-neutral-400">{orders.length} total</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
          <ShoppingCart className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No purchase orders yet</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Once your quote is accepted and a PO is issued, it will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((po) => (
            <button key={po.id} onClick={() => openDetail(po)} className="operations-surface w-full p-4 text-left transition hover:border-neutral-300 dark:hover:border-neutral-600">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getPOLabel(po)}</span>
                <StatusBadge status={po.status} context="supplier" />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                {(po.product_title || po.product_name) && <span className="flex items-center gap-1"><Package className="h-3 w-3" />{po.product_title || po.product_name}</span>}
                {(po.total_price_cents || po.total_amount_cents) && <span className="font-medium text-neutral-700 dark:text-neutral-200">{fmtPeso(po.total_price_cents || po.total_amount_cents)}</span>}
                {po.business_name && <span>{po.business_name}</span>}
                {po.expected_delivery_at && <span>ETA {formatDate(po.expected_delivery_at)}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
